import './standalone.css'
import { log, reason } from '../app/log.js'
import { reloadWhenIdle } from './busy.js'

/**
 * The worker updates itself. `registerType: 'autoUpdate'` with `skipWaiting` and
 * `clientsClaim` (app/vite.config.ts) means a new build's worker activates the moment it is
 * installed and takes over the open tabs, instead of waiting for every window to close, and
 * navigations are network first, so a plain reload in Safari or in the installed app fetches
 * the fresh `index.html` and its hashed chunks rather than the precached shell.
 *
 * The page then reloads once so the running JS matches the build the worker now serves. That
 * reload waits while a ritual, a lesson or a game is in flight and runs at the next route
 * change instead (`busy.ts`). The child is never told any of this.
 */

const logger = log('pwa')

if (import.meta.env.PROD && 'serviceWorker' in navigator) {
  // A page that had no controller is being claimed by the first install: it is already the
  // build it asked for, so there is nothing to catch up with.
  const controlled = navigator.serviceWorker.controller !== null

  navigator.serviceWorker.addEventListener('controllerchange', () => {
    if (!controlled) return
    reloadWhenIdle(() => window.location.reload())
  })

  const register = () => {
    void navigator.serviceWorker
      .register('/sw.js', { scope: '/', updateViaCache: 'none' })
      .then((registration) => {
        // An installed app is resumed far more often than it is reloaded, so ask again every
        // time it comes back to the front.
        document.addEventListener('visibilitychange', () => {
          if (document.visibilityState !== 'visible') return
          void registration.update().catch((error: unknown) => {
            logger.info('the update check could not reach the network', reason(error))
          })
        })
      })
      .catch((error: unknown) => {
        logger.warn('service worker registration failed', reason(error))
      })
  }
  if (document.readyState === 'complete') register()
  else window.addEventListener('load', register, { once: true })
}
